import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Avatar,
  Menu,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  CalendarToday as CalendarIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  FilterList as FilterListIcon,
  Group as GroupIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';
import { LanguageConstants } from '../helpers/LanguageConstants';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import ApprovalActionPreview, { ApprovalActionPreviewItem } from '../components/ApprovalActionPreview';

interface Approval {
  id: string;
  thread_id: string;
  message_id: string;
  channel_id: string;
  channel_name: string;
  requested_by_user_id: string;
  requested_by_user_name: string;
  requested_by_user_email: string;
  approver_user_id: string;
  approver_user_name: string;
  approver_user_email: string;
  status: string;
  title: string;
  description: string;
  approval_notes?: string;
  rejection_reason?: string;
  comment?: string;
  is_active: string;
  created_at: string;
  updated_at: string;
  approved_at?: string;
  rejected_at?: string;
  // Actual data structure from API
  approval_details: {
    status: string;
    description: string;
    approver: {
      name: string;
      id: string;
    };
    requested_by: {
      name: string;
      id: string;
    };
    created_by: {
      name: string;
      id: string;
    };
    created_at: string;
    /** What approving will run (deferred actions), shaped for display by base_shay. */
    action_preview?: ApprovalActionPreviewItem[] | null;
  };
  context: {
    channel?: {
      name: string;
      id: string;
    };
    thread?: {
      id: string;
      title?: string;
      description?: string;
    };
  };
}

interface ApprovalsResponse {
  approvals: Approval[];
  total: number;
  page: number;
  size: number;
}

const ApprovalsPage: React.FC = () => {
  const navigate = useNavigate();
  const { markSessionExpired } = useAuth();
  const { isDarkMode } = useTheme();

  // Navigate to thread and open approval panel; uses channelId/threadId from API context
  const handleApprovalRowClick = (approval: Approval) => {
    const channelId = approval.context?.channel?.id;
    const threadId = approval.context?.thread?.id;
    if (channelId && threadId) {
      navigate(`/threads/${channelId}/${threadId}?openApproval=true`);
    }
  };
  // Function to check if current user is the assigned approver
  const isCurrentUserApprover = (approval: Approval) => {
    // Get user_id from localStorage (from the login response)
    const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
    if (!storedUserData) return false;
    
    try {
      const parsedUserData = JSON.parse(storedUserData);
      const currentUserId = parsedUserData.user_id;
      
      // Compare with approval approver id
      return currentUserId === approval.approval_details.approver.id;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return false;
    }
  };

  // Function to generate consistent avatar colors based on user identifier
  const getAvatarColor = (identifier: string) => {
    const colors = [
      '#1976d2', // Blue
      '#388e3c', // Green
      '#d32f2f', // Red
      '#7b1fa2', // Purple
      '#f57c00', // Orange
      '#00796b', // Teal
      '#c2185b', // Pink
      '#5d4037', // Brown
      '#455a64', // Blue Grey
      '#ff6f00', // Amber
      '#2e7d32', // Dark Green
      '#c62828', // Dark Red
    ];
    
    // Generate a hash from the identifier to get consistent colors
    let hash = 0;
    for (let i = 0; i < identifier.length; i++) {
      hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Use the hash to select a color
    const colorIndex = Math.abs(hash) % colors.length;
    return colors[colorIndex];
  };
  
  // Get logged in user data from localStorage
  const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
  const parsedStoredUserData = storedUserData ? JSON.parse(storedUserData) : null;
  
  // Console log to check what data we're getting from localStorage
  
  // State for users data
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  
  const [allApprovals, setAllApprovals] = useState<Approval[]>([]);
  const [filteredApprovals, setFilteredApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState<boolean>(false); // Start with false since we don't fetch immediately
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    size: 20,
  });

  // Handle page change
  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage + 1 })); // +1 because MUI uses 0-based indexing
  };

  // Handle page size change
  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(event.target.value, 10);
    setPagination(prev => ({ 
      ...prev, 
      size: newSize, 
      page: 1 // Reset to first page when changing page size
    }));
  };
  
  const [filters, setFilters] = useState({
    status: [] as string[],
    workspace_id: '',
    channel_id: '',
    thread_id: '',
    source: '',
    tags: '',
    timeFilter: 'All',
    created_by: '', // This will now store user ID
    requested_by: '', // This will store user ID who requested approval
  });
  const [selectedAssignee, setSelectedAssignee] = useState<{
    name: string;
    email: string;
    id: string;
  } | null>(null);
  const [assigneeMenuAnchor, setAssigneeMenuAnchor] = useState<null | HTMLElement>(null);
  const [timeFilterMenuAnchor, setTimeFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusFilterAnchor, setStatusFilterAnchor] = useState<null | HTMLElement>(null);
  const [assigneeFilterAnchor, setAssigneeFilterAnchor] = useState<null | HTMLElement>(null);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [statusDropdownAnchor, setStatusDropdownAnchor] = useState<null | HTMLElement>(null);
  const [selectedApprovalId, setSelectedApprovalId] = useState<string | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [rowComments, setRowComments] = useState<{ [key: string]: string }>({});
  const [selectedStatuses, setSelectedStatuses] = useState<{ [key: string]: string }>({});
  const [selectedActions, setSelectedActions] = useState<{ [key: string]: string }>({});
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: 'asc' | 'desc' | null;
  }>({ field: '', direction: null });
  const [channelMembers, setChannelMembers] = useState<Array<{ user_id: string; name: string; email_id: string }>>([]);
  const [membersMenuAnchor, setMembersMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedApprovalForReassign, setSelectedApprovalForReassign] = useState<string | null>(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [createdByFilterAnchor, setCreatedByFilterAnchor] = useState<null | HTMLElement>(null);
  const [spaceFilterAnchor, setSpaceFilterAnchor] = useState<null | HTMLElement>(null);

  // Filter users based on search query
  const filteredUsers = users.filter(user => {
    const searchLower = userSearchQuery.toLowerCase();
    const nameMatch = user.name?.toLowerCase().includes(searchLower);
    const emailMatch = user.email_id?.toLowerCase().includes(searchLower) || user.email?.toLowerCase().includes(searchLower);
    return nameMatch || emailMatch;
  });

  // Fetch users by company ID
  const fetchUsers = async () => {
    if (!parsedStoredUserData?.company_id) {
      return;
    }
    
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USERS.LIST_COMPANY_USERS(parsedStoredUserData.company_id)}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch approvals
  const fetchApprovals = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      
      // Add pagination parameters
      params.append('page', pagination.page.toString());
      params.append('size', pagination.size.toString());
      
      // Add filter parameters including status filter
      if (filters.status.length > 0) params.append('status_filter', filters.status.join(','));
      if (filters.created_by) params.append('created_by', filters.created_by);
      if (filters.requested_by) params.append('requested_by', filters.requested_by);
      if (filters.workspace_id) params.append('workspace_id', filters.workspace_id);
      if (filters.channel_id) params.append('channel_id', filters.channel_id);
      if (filters.thread_id) params.append('thread_id', filters.thread_id);
      if (filters.source) params.append('source', filters.source);
      if (filters.tags) params.append('tags', filters.tags);

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APPROVALS.LIST}?${params}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ApprovalsResponse = await response.json();
      setAllApprovals(data.approvals || []);
      setFilteredApprovals(data.approvals || []);
      setPagination(prev => ({
        ...prev,
        total: data.total,
        page: data.page,
        size: data.size,
      }));

    } catch (err) {
      let errorMessage = 'Failed to fetch approvals';
      
      if (err instanceof Error) {
        if (err.message.includes('HTTP error! status:')) {
          const statusMatch = err.message.match(/status: (\d+)/);
          const statusCode = statusMatch ? statusMatch[1] : '';
          
          switch (statusCode) {
            case '401':
              markSessionExpired();
              return;
            case '403':
              errorMessage = 'You do not have permission to access approvals.';
              break;
            case '500':
              errorMessage = 'Server error. Please try again later.';
              break;
            default:
              errorMessage = `Failed to load approvals (Error ${statusCode}). Please try again.`;
          }
        } else if (err.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      console.error('Failed to fetch approvals:', err);
    } finally {
      setLoading(false);
    }
  };

  // Set default assignee to logged-in user when data is available
  useEffect(() => {
    if (parsedStoredUserData && !selectedAssignee) {
      setSelectedAssignee({
        name: parsedStoredUserData.name || 'Unknown User',
        email: parsedStoredUserData.email_id || 'No email',
        id: parsedStoredUserData.user_id || ''
      });
      // Mark as initialized so we can start fetching approvals
      setIsInitialized(true);
    }
  }, [parsedStoredUserData, selectedAssignee]);

  // Users API is now only called when dropdown is opened
  // Removed automatic fetch on component mount

  // Main effect for fetching approvals and handling filters
  useEffect(() => {
    
    // Only fetch approvals if we have a selected assignee and we're initialized
    if (selectedAssignee?.id && isInitialized) {
      fetchApprovals();
    }
  }, [pagination.page, pagination.size, selectedAssignee?.id, filters.status, filters.created_by, filters.requested_by, filters.channel_id, isInitialized]);

  // Reset to page 1 when filters change (except pagination filters)
  useEffect(() => {
    if (pagination.page !== 1) {
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  }, [filters.status, selectedAssignee?.id, filters.workspace_id, filters.channel_id, filters.thread_id, filters.source, filters.tags, filters.timeFilter, filters.created_by, filters.requested_by]);

  // Local filtering effect
  useEffect(() => {
    let filtered = [...allApprovals];

    // Status filtering is now handled by the API
    // Removed local status filtering

    // Apply requested_by filter locally
    if (filters.requested_by) {
        filtered = filtered.filter(approval => 
          approval.approval_details.requested_by.id === filters.requested_by
        );
    }

    // Apply created_by filter locally
    if (filters.created_by) {
        filtered = filtered.filter(approval => 
          approval.approval_details.created_by.id === filters.created_by
        );
    }

    // Channel filtering is now handled by the API
    // Removed local channel filtering

    // Apply time filter locally
    if (filters.timeFilter && filters.timeFilter !== 'All') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      filtered = filtered.filter(approval => {
        const approvalDate = new Date(approval.approval_details.created_at);
        const approvalDateOnly = new Date(approvalDate.getFullYear(), approvalDate.getMonth(), approvalDate.getDate());
        
        switch (filters.timeFilter) {
          case 'Today':
            return approvalDateOnly.getTime() === today.getTime();
          case 'Yesterday':
            return approvalDateOnly.getTime() === yesterday.getTime();
          case 'Last 24 hrs':
            const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            return approvalDate >= twentyFourHoursAgo;
          case 'Last 48 hrs':
            const fortyEightHoursAgo = new Date(now.getTime() - 48 * 60 * 60 * 1000);
            return approvalDate >= fortyEightHoursAgo;
          case 'Last 7 Days':
            const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return approvalDate >= sevenDaysAgo;
          case 'Last 30 Days':
            const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return approvalDate >= thirtyDaysAgo;
          case 'This Month':
            const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            return approvalDate >= firstDayOfMonth;
          case 'Last Month':
            const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
            return approvalDate >= firstDayOfLastMonth && approvalDate <= lastDayOfLastMonth;
          default:
            if (filters.timeFilter.startsWith('Custom:')) {
              // Handle custom date range
              const customRangeMatch = filters.timeFilter.match(/Custom: (.+) to (.+)/);
              if (customRangeMatch) {
                const startDate = new Date(customRangeMatch[1]);
                const endDate = new Date(customRangeMatch[2]);
                endDate.setHours(23, 59, 59, 999); // Set to end of day
                return approvalDate >= startDate && approvalDate <= endDate;
              }
            }
            return true;
        }
      });
    }

    // Apply sorting
    if (sortConfig.field && sortConfig.direction) {
      filtered.sort((a, b) => {
        let aValue: any;
        let bValue: any;
        
        if (sortConfig.field === 'created_at') {
          aValue = new Date(a.approval_details.created_at).getTime();
          bValue = new Date(b.approval_details.created_at).getTime();
        }
        
        if (sortConfig.direction === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      });
    }

    // Deduplicate by id so key={approval.id} stays unique (first occurrence kept)
    const seenIds = new Set<string>();
    filtered = filtered.filter(a => {
      if (seenIds.has(a.id)) return false;
      seenIds.add(a.id);
      return true;
    });

    setFilteredApprovals(filtered);
  }, [allApprovals, filters.status, filters.requested_by, filters.channel_id, filters.created_by, filters.timeFilter, sortConfig]);

  const handleFilterChange = (field: string, value: string | string[]) => {
    setFilters(prev => ({
        ...prev,
        [field]: value
    }));
  };

  const handleStatusToggle = (status: string) => {
    setFilters(prev => {
      const currentStatuses = prev.status;
      const isSelected = currentStatuses.includes(status);
      
      if (isSelected) {
        // Remove status if already selected
        return {
          ...prev,
          status: currentStatuses.filter(s => s !== status)
        };
      } else {
        // Add status if not selected
        return {
          ...prev,
          status: [...currentStatuses, status]
        };
      }
    });
    // Auto-close the status dropdown
    setStatusFilterAnchor(null);
  };

     const handleSelectAllStatuses = () => {
     setFilters(prev => {
       const allStatuses = ['pending', 'approved', 'rejected'];
       const currentStatuses = prev.status;
       
       // If all statuses are already selected, clear all
       if (currentStatuses.length === allStatuses.length) {
         return {
           ...prev,
           status: []
         };
       } else {
         // Select all statuses
         return {
           ...prev,
           status: allStatuses
         };
       }
     });
     // Auto-close the status dropdown
     setStatusFilterAnchor(null);
   };

  const handleSort = (field: string) => {
    setSortConfig(prev => {
      if (prev.field === field) {
        // Same field, cycle through: null -> desc -> asc -> null
        if (prev.direction === null) {
          return { field, direction: 'desc' };
        } else if (prev.direction === 'desc') {
          return { field, direction: 'asc' };
        } else {
          return { field: '', direction: null };
        }
      } else {
        // New field, start with desc
        return { field, direction: 'desc' };
      }
    });
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) {
      // No sort applied - show default chevron down
      return <ExpandMoreIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />;
    }
    
    if (sortConfig.direction === 'desc') {
      // Descending - show chevron down
      return <ExpandMoreIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />;
    }
    
    if (sortConfig.direction === 'asc') {
      // Ascending - show chevron up
      return <ExpandLessIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />;
    }
    
    return null;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'warning';
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleApprove = async (approvalId: string) => {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APPROVALS.ACTION(approvalId)}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          action: 'approve',
          comment: rowComments[approvalId] || ''
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Only update status after successful API call and close expanded box
      setSelectedStatuses(prev => ({ ...prev, [approvalId]: 'approved' }));
      setSelectedActions(prev => ({ ...prev, [approvalId]: '' }));
      setRowComments(prev => ({ ...prev, [approvalId]: '' }));
      
      // Close the expanded box after successful API call
      setTimeout(() => {
        setSelectedStatuses(prev => {
          const newStatuses = { ...prev };
          delete newStatuses[approvalId];
          return newStatuses;
        });
        setSelectedActions(prev => {
          const newActions = { ...prev };
          delete newActions[approvalId];
          return newActions;
        });
      }, 1000); // Close after 1 second to show success state
      
      // Refresh the approvals list
      fetchApprovals();
    } catch (error) {
      console.error('Failed to approve:', error);
    }
  };

  const handleReject = async (approvalId: string) => {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APPROVALS.ACTION(approvalId)}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          action: 'reject',
          rejection_reason: rowComments[approvalId] || 'Rejected by approver'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Only update status after successful API call and close expanded box
      setSelectedStatuses(prev => ({ ...prev, [approvalId]: 'rejected' }));
      setSelectedActions(prev => ({ ...prev, [approvalId]: '' }));
      setRowComments(prev => ({ ...prev, [approvalId]: '' }));
      
      // Close the expanded box after successful API call
      setTimeout(() => {
        setSelectedStatuses(prev => {
          const newStatuses = { ...prev };
          delete newStatuses[approvalId];
          return newStatuses;
        });
        setSelectedActions(prev => {
          const newActions = { ...prev };
          delete newActions[approvalId];
          return newActions;
        });
      }, 1000); // Close after 1 second to show success state
      
      // Refresh the approvals list
      fetchApprovals();
    } catch (error) {
      console.error('Failed to reject:', error);
    }
  };

  const handleReassign = async (approvalId: string) => {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APPROVALS.REASSIGN(approvalId)}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ comment: rowComments[approvalId] || '' })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Only update status after successful API call and close expanded box
      setSelectedStatuses(prev => ({ ...prev, [approvalId]: 'pending' }));
      setSelectedActions(prev => ({ ...prev, [approvalId]: '' }));
      setRowComments(prev => ({ ...prev, [approvalId]: '' }));
      
      // Close the expanded box after successful API call
      setTimeout(() => {
        setSelectedStatuses(prev => {
          const newStatuses = { ...prev };
          delete newStatuses[approvalId];
          return newStatuses;
        });
        setSelectedActions(prev => {
          const newActions = { ...prev };
          delete newActions[approvalId];
          return newActions;
        });
      }, 1000); // Close after 1 second to show success state
      
      // Refresh the approvals list
      fetchApprovals();
    } catch (error) {
      console.error('Failed to reassign:', error);
    }
  };

  const handleSave = async (approvalId: string) => {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APPROVALS.COMMENT(approvalId)}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ comment: rowComments[approvalId] || '' })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Refresh the approvals list
      fetchApprovals();
      // Clear the comment for this specific row
      setRowComments(prev => ({ ...prev, [approvalId]: '' }));
    } catch (error) {
      console.error('Failed to save comment:', error);
    }
  };

  const handleCancel = (approvalId: string) => {
    setRowComments(prev => ({ ...prev, [approvalId]: '' }));
    setSelectedStatuses(prev => ({ ...prev, [approvalId]: '' }));
    setSelectedActions(prev => ({ ...prev, [approvalId]: '' }));
  };

  const handleStatusSelect = (approvalId: string, status: string) => {
    // Close any previously expanded row and expand the new one
    setSelectedStatuses(prev => {
      const newStatuses = { ...prev };
      // Clear all previous selections
      Object.keys(newStatuses).forEach(key => {
        if (key !== approvalId) {
          delete newStatuses[key];
        }
      });
      // Set the new selection
      newStatuses[approvalId] = status;
      return newStatuses;
    });
    setSelectedActions(prev => {
      const newActions = { ...prev };
      // Clear all previous actions
      Object.keys(newActions).forEach(key => {
        if (key !== approvalId) {
          delete newActions[key];
        }
      });
      // Set the new action
      newActions[approvalId] = status;
      return newActions;
    });

    // Scroll to the expanded row after a short delay to ensure DOM is updated
    setTimeout(() => {
      const rowElement = document.querySelector(`[data-approval-id="${approvalId}"]`);
      if (rowElement) {
        rowElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  const handleActionSelect = (approvalId: string, action: string) => {
    
    // Only update local state, don't trigger API calls
    setSelectedActions(prev => ({ ...prev, [approvalId]: action }));
    
    // Clear rowComments when switching to approve/reject actions
    if (action === 'approved' || action === 'rejected') {
      setRowComments(prev => ({ ...prev, [approvalId]: '' }));
    }
    
    // If reassign is selected, fetch members immediately
    if (action === 'reassign') {
      const approval = allApprovals.find(a => a.id === approvalId);
      if (approval && approval.context && approval.context.channel && approval.context.channel.id) {
        fetchChannelMembers(approval.context.channel.id);
      }
    }
  };

    const fetchChannelMembers = async (channelId: string) => {
    try {
      setLoadingMembers(true);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}/api/v1/channels/${channelId}/members`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Handle different response formats
      let membersArray = [];
      if (Array.isArray(data)) {
        membersArray = data;
      } else if (data && Array.isArray(data.members)) {
        membersArray = data.members;
      } else if (data && Array.isArray(data.data)) {
        membersArray = data.data;
      } else if (data && typeof data === 'object') {
        // If it's an object, try to find any array property
        const arrayProps = Object.values(data).filter(val => Array.isArray(val));
        if (arrayProps.length > 0) {
          membersArray = arrayProps[0];
        }
      }
      
      setChannelMembers(membersArray);
    } catch (error) {
      console.error('Failed to fetch channel members:', error);
      setChannelMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleReassignClick = (approvalId: string, channelId: string, event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setSelectedApprovalForReassign(approvalId);
    setMembersMenuAnchor(event.currentTarget);
    fetchChannelMembers(channelId);
  };

  const handleMemberSelect = (memberId: string, memberName: string) => {
    if (selectedApprovalForReassign) {
      // Set the selected member for the approval
      setSelectedActions(prev => ({ ...prev, [selectedApprovalForReassign]: 'reassign' }));
      setRowComments(prev => ({ ...prev, [selectedApprovalForReassign]: memberId }));
      
      // Close the menu
      setMembersMenuAnchor(null);
      setSelectedApprovalForReassign(null);
    }
  };

  const handleReassignSubmit = async (approvalId: string) => {
    try {
      const selectedMemberId = rowComments[approvalId];
      if (!selectedMemberId) {
        console.error('No member selected for reassignment');
        return;
      }

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APPROVALS.REASSIGN(approvalId)}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          new_approver_user_id: selectedMemberId,
          notes: 'Reassigned by current approver'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Close the expanded box after successful API call
      setTimeout(() => {
        setSelectedStatuses(prev => {
          const newStatuses = { ...prev };
          delete newStatuses[approvalId];
          return newStatuses;
        });
        setSelectedActions(prev => {
          const newActions = { ...prev };
          delete newActions[approvalId];
          return newActions;
        });
        setRowComments(prev => {
          const newComments = { ...prev };
          delete newComments[approvalId];
          return newComments;
        });
      }, 1000);

      // Refresh the approvals list
      fetchApprovals();
    } catch (error) {
      console.error('Failed to reassign:', error);
    }
  };

  const getUniqueCreators = () => {
    const uniqueCreatorsMap = new Map();
    allApprovals.forEach(approval => {
      const creator = approval.approval_details.requested_by;
      if (creator.id && creator.name) {
        uniqueCreatorsMap.set(creator.id, creator.name);
      }
    });
    return Array.from(uniqueCreatorsMap.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  };

const getUniqueApprovers = () => {
  const uniqueApprovers = Array.from(new Set(allApprovals.map(approval => approval.approval_details.approver.name)));
  return uniqueApprovers.sort();
};

const getUniqueRequesters = () => {
  const uniqueRequestersMap = new Map();
  allApprovals.forEach(approval => {
    const requester = approval.approval_details.requested_by;
    if (requester.id && requester.name) {
      uniqueRequestersMap.set(requester.id, requester.name);
    }
  });
  return Array.from(uniqueRequestersMap.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
};

const getUniqueSpaces = () => {
  const uniqueSpacesMap = new Map();
  allApprovals.forEach(approval => {
    const channel = approval.context?.channel;
    if (channel?.id && channel?.name) {
      uniqueSpacesMap.set(channel.id, channel.name);
    }
  });
  return Array.from(uniqueSpacesMap.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
};

  // Don't show full-screen loading - keep existing data visible
  // Loading state will be shown as a small icon in the header

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={fetchApprovals} variant="contained">
          Retry
        </Button>
      </Box>
    );
  }

    return (
    <Box sx={{ px: 1, height: '100%', overflow: 'hidden' }}>
            {/* Approvals Table */}
      <Paper sx={{ bgcolor: 'background.paper' }}>
        {/* User Info Row */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 2, 
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: isDarkMode ? 'grey.800' : '#f8f9fa'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
              Assignee:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ 
                width: 24, 
                height: 24, 
                bgcolor: getAvatarColor(selectedAssignee?.name || selectedAssignee?.email || ''),
                fontSize: '12px', 
                color: 'white' 
              }}>
                {selectedAssignee?.name ? selectedAssignee.name.charAt(0).toUpperCase() : 'U'}
              </Avatar>
              <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                {selectedAssignee?.name || 'Loading...'}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => {
                  setAssigneeMenuAnchor(e.currentTarget);
                  // Fetch users when dropdown is opened
                  if (!assigneeMenuAnchor) {
                    fetchUsers();
                  }
                }}
                disabled={parsedStoredUserData?.role !== 'admin'}
                sx={{ 
                  ml: 1,
                  opacity: parsedStoredUserData?.role === 'admin' ? 1 : 0.5,
                  cursor: parsedStoredUserData?.role === 'admin' ? 'pointer' : 'not-allowed'
                }}
                title={parsedStoredUserData?.role === 'admin' ? 'Select assignee' : 'Only admins can change assignee'}
              >
                {assigneeMenuAnchor ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
              </IconButton>
              
              {/* Loading indicator next to dropdown */}
              {loading && (
                <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                </Box>
              )}
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={(e) => setTimeFilterMenuAnchor(e.currentTarget)}
              sx={{
                textTransform: 'none',
                borderColor: 'divider',
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CalendarIcon fontSize="small" />
              {filters.timeFilter}
              <ArrowDownIcon fontSize="small" />
            </Button>
          </Box>
        </Box>

        {/* Users Dropdown Menu */}
        <Menu
          anchorEl={assigneeMenuAnchor}
          open={Boolean(assigneeMenuAnchor)}
          onClose={() => setAssigneeMenuAnchor(null)}
          PaperProps={{
            sx: {
              maxHeight: '60vh',
              width: 350,
              mt: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }
          }}
        >
          {/* Search Field - Sticky Header */}
          <Box sx={{ 
            p: 2, 
            borderBottom: 1,
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            backgroundColor: 'background.paper',
            zIndex: 2
          }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search users..."
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  height: '40px'
                }
              }}
            />
          </Box>
          {/* Scrollable User List */}
          <Box sx={{ 
            height: 'calc(60vh - 80px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: 1
          }}>
            {loadingUsers ? (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={20} />
              </Box>
            ) : filteredUsers.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {userSearchQuery ? 'No users found matching your search' : 'No users found'}
                </Typography>
              </Box>
            ) : (
              filteredUsers.map((user, index) => (
                <MenuItem
                  key={user.id || index}
                  onClick={() => {
                    setSelectedAssignee({
                      name: user.name || 'Unknown User',
                      email: user.email_id || user.email || 'No email',
                      id: user.id || user.resourceId || ''
                    });
                    setAssigneeMenuAnchor(null);
                    // The useEffect will automatically refetch approvals with the new assignee filter
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderBottom: index < filteredUsers.length - 1 ? 1 : 0,
                    borderColor: 'divider'
                  }}
                >
                                   <Avatar sx={{ 
                     width: 32, 
                     height: 32, 
                     bgcolor: getAvatarColor(user.name || user.email_id || user.email || ''),
                     fontSize: '14px', 
                     color: 'white' 
                   }}>
                     {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                   </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {user.name || 'Unknown User'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.email_id || user.email || 'No email'}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            )}
          </Box>
        </Menu>

                                   <TableContainer sx={{ 
            '& .MuiTable-root': {
              minWidth: '100%'
            }
          }}>
          <Table sx={{ width: '100%' }}>
                         <TableHead sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
               <TableRow sx={{ bgcolor: 'background.paper' }}>
                                                   <TableCell sx={{ 
                    fontWeight: 'bold', 
                    color: 'primary.main',
                    width: '20%'
                  }}>
                    Description
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 'bold', 
                    color: 'primary.main',
                    width: '15%'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Status
                      </Typography>
                      <IconButton 
                        size="small" 
                        sx={{ ml: -0.25 }}
                        onClick={(e) => setStatusFilterAnchor(e.currentTarget)}
                        title="Filter by Status"
                      >
                        <FilterListIcon 
                          fontSize="small" 
                          sx={{ 
                            color: filters.status.length > 0 ? '#1976d2' : '#9e9e9e'
                          }} 
                        />
                      </IconButton>
                      {filters.status.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => handleFilterChange('status', [])}
                          sx={{ p: 0.5 }}
                          title="Clear Status Filter"
                        >
                          <Box sx={{ position: 'relative' }}>
                            <FilterListIcon 
                              fontSize="small" 
                              sx={{ 
                                color: '#9e9e9e',
                                opacity: 0.7
                              }} 
                            />
                            {/* Cut line overlay */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                width: '80%',
                                height: '2px',
                                backgroundColor: '#9e9e9e',
                                transform: 'translateX(-50%) rotate(-45deg)',
                                opacity: 0.8
                              }}
                            />
                          </Box>
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 'bold', 
                    color: 'primary.main',
                    width: '18%'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Created By
                      </Typography>
                      <IconButton 
                        size="small" 
                        sx={{ ml: -0.25 }} 
                        title="Filter by Created By"
                        onClick={(e) => setCreatedByFilterAnchor(e.currentTarget)}
                      >
                        <FilterListIcon 
                          fontSize="small" 
                          sx={{ 
                            color: filters.created_by ? '#1976d2' : '#9e9e9e'
                          }} 
                        />
                      </IconButton>
                      {filters.created_by && (
                        <IconButton
                          size="small"
                          onClick={() => handleFilterChange('created_by', '')}
                          sx={{ p: 0.5 }}
                          title="Clear Created By Filter"
                        >
                          <Box sx={{ position: 'relative' }}>
                            <FilterListIcon 
                              fontSize="small" 
                              sx={{ 
                                color: '#9e9e9e',
                                opacity: 0.7
                              }} 
                            />
                            {/* Cut line overlay */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                width: '80%',
                                height: '2px',
                                backgroundColor: '#9e9e9e',
                                transform: 'translateX(-50%) rotate(-45deg)',
                                opacity: 0.8
                              }}
                            />
                          </Box>
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 'bold', 
                    color: 'primary.main',
                    width: '18%'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Assigned By
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={(e) => setAssigneeFilterAnchor(e.currentTarget)}
                        sx={{ ml: -0.25 }}
                        title="Filter by Assigned By"
                      >
                        <FilterListIcon 
                          fontSize="small" 
                          sx={{ 
                            color: filters.requested_by ? '#1976d2' : '#9e9e9e'
                          }} 
                        />
                      </IconButton>
                      {filters.requested_by && (
                        <IconButton
                          size="small"
                          onClick={() => handleFilterChange('requested_by', '')}
                          sx={{ p: 0.5 }}
                          title="Clear Assigned By Filter"
                        >
                          <Box sx={{ position: 'relative' }}>
                            <FilterListIcon 
                              fontSize="small" 
                              sx={{ 
                                color: '#9e9e9e',
                                opacity: 0.7
                              }} 
                            />
                            {/* Cut line overlay */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                width: '80%',
                                height: '2px',
                                backgroundColor: '#9e9e9e',
                                transform: 'translateX(-50%) rotate(-45deg)',
                                opacity: 0.8
                              }}
                            />
                          </Box>
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 'bold', 
                    color: 'primary.main',
                    width: '14%'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Created Date
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => handleSort('created_at')}
                        sx={{ p: 0.5 }}
                        title="Sort by Created Date"
                      >
                        {getSortIcon('created_at')}
                      </IconButton>
                    </Box>
                  </TableCell>
                                   <TableCell sx={{ 
                    fontWeight: 'bold', 
                    color: 'primary.main',
                    width: '15%'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {LanguageConstants.topic}
                      </Typography>
                      <IconButton 
                        size="small" 
                        sx={{ ml: -0.25 }} 
                        title={`Filter by ${LanguageConstants.topic}`}
                        onClick={(e) => setSpaceFilterAnchor(e.currentTarget)}
                      >
                        <FilterListIcon 
                          fontSize="small" 
                          sx={{ 
                            color: filters.channel_id ? '#1976d2' : '#9e9e9e'
                          }} 
                        />
                      </IconButton>
                      {filters.channel_id && (
                        <IconButton
                          size="small"
                          onClick={() => handleFilterChange('channel_id', '')}
                          sx={{ p: 0.5 }}
                          title={`Clear ${LanguageConstants.topic} Filter`}
                        >
                          <Box sx={{ position: 'relative' }}>
                            <FilterListIcon 
                              fontSize="small" 
                              sx={{ 
                                color: '#9e9e9e',
                                opacity: 0.7
                              }} 
                            />
                            {/* Cut line overlay */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                width: '80%',
                                height: '2px',
                                backgroundColor: '#9e9e9e',
                                transform: 'translateX(-50%) rotate(-45deg)',
                                opacity: 0.8
                              }}
                            />
                          </Box>
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>

              </TableRow>
            </TableHead>
            <TableBody>
              {filteredApprovals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No approvals found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                                filteredApprovals.map((approval, index) => (
                  <React.Fragment key={approval.id}>
                                         <TableRow 
                       hover
                       data-approval-id={approval.id}
                       onClick={() => handleApprovalRowClick(approval)}
                       sx={{ 
                         backgroundColor: index % 2 === 0
                           ? (isDarkMode ? 'grey.800' : '#E0F7FA')
                           : (isDarkMode ? 'grey.900' : '#fff'),
                         border: expandedRowId === approval.id ? '2px solid' : 'none',
                         borderColor: expandedRowId === approval.id ? 'primary.main' : 'transparent',
                         cursor: approval.context?.channel?.id && approval.context?.thread?.id ? 'pointer' : 'default'
                       }}
                     >
                     <TableCell 
                       sx={{ 
                         maxWidth: 0,
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap'
                       }}
                       title={approval.approval_details.description}
                     >
                       <Typography variant="body2" color="text.secondary" sx={{
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap'
                       }}>
                         {approval.approval_details.description}
                       </Typography>
                     </TableCell>
                                           <TableCell 
                        sx={{ 
                          maxWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={approval.approval_details.status}
                      >
                        <Button
                          variant="outlined"
                                                      size="small"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click redirect when using status dropdown
                              if (approval.approval_details.status !== 'approved' && 
                                  approval.approval_details.status !== 'rejected' && 
                                  isCurrentUserApprover(approval)) {
                                setStatusDropdownAnchor(e.currentTarget);
                                setSelectedApprovalId(approval.id);
                              }
                            }}
                            disabled={!isCurrentUserApprover(approval)}
                            sx={{
                              borderRadius: '16px',
                              textTransform: 'none',
                              border: 'none',
                              color: approval.approval_details.status === 'pending' ? '#ff9800' :
                                     approval.approval_details.status === 'approved' ? (isDarkMode ? '#81c784' : '#2e7d32') : '#ef5350',
                              backgroundColor: approval.approval_details.status === 'pending' ? (isDarkMode ? 'grey.700' : '#fff3e0') :
                                             approval.approval_details.status === 'approved' ? (isDarkMode ? 'grey.700' : '#e8f5e8') : (isDarkMode ? 'grey.700' : '#ffebee'),
                              cursor: (approval.approval_details.status === 'approved' ||
                                      approval.approval_details.status === 'rejected' ||
                                      !isCurrentUserApprover(approval)) ? 'default' : 'pointer',
                              px: 2,
                              py: 0.5,
                              fontWeight: 'medium',
                              opacity: isCurrentUserApprover(approval) ? 1 : 0.5,
                              '&:hover': {
                                backgroundColor: (approval.approval_details.status === 'pending' && isCurrentUserApprover(approval)) ? (isDarkMode ? 'grey.600' : '#ffe0b2') :
                                               approval.approval_details.status === 'approved' ? (isDarkMode ? 'grey.700' : '#e8f5e8') : (isDarkMode ? 'grey.700' : '#ffebee')
                              },
                              '&:disabled': {
                                backgroundColor: approval.approval_details.status === 'pending' ? (isDarkMode ? 'grey.700' : '#fff3e0') :
                                               approval.approval_details.status === 'approved' ? (isDarkMode ? 'grey.700' : '#e8f5e8') : (isDarkMode ? 'grey.700' : '#ffebee'),
                                color: approval.approval_details.status === 'pending' ? '#ff9800' :
                                       approval.approval_details.status === 'approved' ? (isDarkMode ? '#81c784' : '#2e7d32') : '#ef5350',
                                opacity: 0.5,
                                cursor: 'not-allowed'
                              }
                            }}
                          >
                            {approval.approval_details.status.charAt(0).toUpperCase() + approval.approval_details.status.slice(1)}
                            {(approval.approval_details.status !== 'approved' && 
                              approval.approval_details.status !== 'rejected' && 
                              isCurrentUserApprover(approval)) && (
                              <ArrowDownIcon fontSize="small" sx={{ ml: 0.5, color: 'inherit' }} />
                            )}
                        </Button>
                      </TableCell>
                     <TableCell 
                       sx={{ 
                         maxWidth: 0,
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap'
                       }}
                       title={approval.approval_details.requested_by.name}
                     >
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                         <Avatar 
                           sx={{ 
                             width: 24, 
                             height: 24, 
                             bgcolor: getAvatarColor(approval.approval_details.requested_by.name),
                             fontSize: '10px' 
                           }}
                         >
                           {approval.approval_details.requested_by.name.charAt(0).toUpperCase()}
                         </Avatar>
                         <Typography 
                           variant="body2"
                           sx={{
                             overflow: 'hidden',
                             textOverflow: 'ellipsis',
                             whiteSpace: 'nowrap'
                           }}
                         >
                           {approval.approval_details.requested_by.name}
                         </Typography>
                       </Box>
                     </TableCell>
                     <TableCell 
                       sx={{ 
                         maxWidth: 0,
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap'
                       }}
                       title={approval.approval_details.approver.name}
                     >
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                         <Avatar 
                           sx={{ 
                             width: 24, 
                             height: 24, 
                             bgcolor: getAvatarColor(approval.approval_details.approver.name),
                             fontSize: '10px' 
                           }}
                         >
                           {approval.approval_details.approver.name.charAt(0).toUpperCase()}
                         </Avatar>
                         <Typography 
                           variant="body2"
                           sx={{
                             overflow: 'hidden',
                             textOverflow: 'ellipsis',
                             whiteSpace: 'nowrap'
                           }}
                         >
                           {approval.approval_details.approver.name}
                         </Typography>
                       </Box>
                     </TableCell>
                     <TableCell 
                       sx={{ 
                         maxWidth: 0,
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap'
                       }}
                       title={new Date(approval.approval_details.created_at).toLocaleDateString('en-GB', {
                         day: '2-digit',
                         month: 'short',
                         year: 'numeric'
                       })}
                     >
                       <Typography variant="body2" sx={{
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap'
                       }}>
                         {new Date(approval.approval_details.created_at).toLocaleDateString('en-GB', {
                           day: '2-digit',
                           month: 'short',
                           year: 'numeric'
                         })}
                       </Typography>
                     </TableCell>
                     <TableCell 
                       sx={{ 
                         maxWidth: 0,
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap'
                       }}
                       title={approval.context?.channel?.name}
                     >
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                         <GroupIcon fontSize="small" sx={{ color: 'primary.main' }} />
                         <Typography 
                           variant="body2" 
                           sx={{ 
                             color: 'primary.main',
                             overflow: 'hidden',
                             textOverflow: 'ellipsis',
                             whiteSpace: 'nowrap'
                           }}
                         >
                           {approval.context?.channel?.name ?? `No ${LanguageConstants.topic}`}
                         </Typography>
                       </Box>
                                           </TableCell>
                    </TableRow>

                    {/* What approving will run — visible straight away on every pending approval
                        that carries an action, so the approver doesn't have to pick Approve/Reject
                        first. Settled (approved/rejected) rows stay compact. */}
                    {approval.approval_details.status === 'pending' &&
                      (approval.approval_details.action_preview?.length ?? 0) > 0 && (
                      <TableRow data-testid="approval-preview-row">
                        <TableCell colSpan={6} sx={{ p: 0, border: 'none' }}>
                          <Box sx={{ px: 1.5, pt: 1.5, backgroundColor: isDarkMode ? 'grey.800' : '#f8f9fa' }}>
                            {/* Collapsed to a one-line summary: a bundle is tall and would push the
                                list down. The titles stay visible; the down arrow opens the details. */}
                            <ApprovalActionPreview actions={approval.approval_details.action_preview} collapsible />
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}

                                         {/* Action Buttons and Comment Row - Only show when status is selected */}
                     {selectedStatuses[approval.id] && (
                       <TableRow>
                         <TableCell colSpan={6} sx={{ p: 0, border: 'none' }}>
                                                       <Box sx={{ 
                              p: 1.5, 
                              backgroundColor: isDarkMode ? 'grey.800' : '#f8f9fa',
                              borderTop: 1,
                              borderColor: 'divider'
                            }}>
                                                            {/* Action Buttons */}
                               <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                                 <Button
                                   size="small"
                                   variant={selectedActions[approval.id] === 'approved' ? "contained" : "outlined"}
                                   startIcon={<ApproveIcon />}
                                   onClick={() => handleActionSelect(approval.id, 'approved')}
                                   sx={{
                                     backgroundColor: selectedActions[approval.id] === 'approved' ? '#2e7d32' : 'transparent',
                                     borderColor: selectedActions[approval.id] === 'approved' ? '#2e7d32' : '#2e7d32',
                                     color: selectedActions[approval.id] === 'approved' ? 'white' : '#2e7d32',
                                     fontSize: '0.75rem',
                                     px: 1.5,
                                     py: 0.5,
                                     '&:hover': { 
                                       backgroundColor: selectedActions[approval.id] === 'approved' ? '#1b5e20' : (isDarkMode ? 'grey.700' : '#e8f5e8'),
                                       borderColor: '#1b5e20'
                                     }
                                   }}
                                 >
                                   Approve
                                 </Button>
                                 <Button
                                   size="small"
                                   variant={selectedActions[approval.id] === 'rejected' ? "contained" : "outlined"}
                                   startIcon={<RejectIcon />}
                                   onClick={() => handleActionSelect(approval.id, 'rejected')}
                                   sx={{
                                     backgroundColor: selectedActions[approval.id] === 'rejected' ? '#d32f2f' : 'transparent',
                                     borderColor: selectedActions[approval.id] === 'rejected' ? '#d32f2f' : '#d32f2f',
                                     color: selectedActions[approval.id] === 'rejected' ? 'white' : '#d32f2f',
                                     fontSize: '0.75rem',
                                     px: 1.5,
                                     py: 0.5,
                                    '&:hover': { 
                                      backgroundColor: selectedActions[approval.id] === 'rejected' ? '#c62828' : (isDarkMode ? 'grey.700' : '#ffebee'),
                                      borderColor: '#c62828'
                                    }
                                   }}
                                 >
                                   Reject
                                 </Button>
                                                                                                                                       <Button
                                     size="small"
                                     variant={selectedActions[approval.id] === 'reassign' ? "contained" : "outlined"}
                                     startIcon={<ArrowDownIcon />}
                                     onClick={() => {
                                       handleActionSelect(approval.id, 'reassign');
                                     }}
                                    sx={{
                                      backgroundColor: selectedActions[approval.id] === 'reassign' ? '#ff9800' : 'transparent',
                                      borderColor: selectedActions[approval.id] === 'reassign' ? '#ff9800' : '#ff9800',
                                      color: selectedActions[approval.id] === 'reassign' ? 'white' : '#ff9800',
                                      fontSize: '0.75rem',
                                      px: 1.5,
                                      py: 0.5,
                                      '&:hover': { 
                                        backgroundColor: selectedActions[approval.id] === 'reassign' ? '#f57c00' : (isDarkMode ? 'grey.700' : '#fff3e0'),
                                        borderColor: '#f57c00'
                                      }
                                    }}
                                  >
                                    Reassign
                                  </Button>
                               </Box>
                               
                               
                              
                                                             {/* Comment Section or Member Selection */}
                               <Box sx={{ mb: 1.5 }}>
                                                                   {selectedActions[approval.id] === 'reassign' ? (
                                                                         <Box>
                                       <select
                                         value={rowComments[approval.id] || ''}
                                         onChange={(e) => setRowComments(prev => ({ ...prev, [approval.id]: e.target.value }))}
                                         disabled={loadingMembers}
                                         style={{
                                           width: '100%',
                                           padding: '8px 12px',
                                           fontSize: '14px',
                                           border: '1px solid',
                                           borderColor: isDarkMode ? 'rgba(255,255,255,0.23)' : '#d1d5db',
                                           borderRadius: '4px',
                                           backgroundColor: isDarkMode ? '#1e1e1e' : 'white',
                                           color: isDarkMode ? '#fff' : '#000000',
                                           cursor: 'pointer',
                                           height: '38px'
                                         }}
                                         
                                       >
                                                                                                                       {loadingMembers ? (
                                          <option disabled>Loading members...</option>
                                        ) : (
                                          <>
                                                                                         <option value="" disabled style={{ color: '#999', fontStyle: 'italic' }}>
                                               Select Member to Reassign *
                                             </option>
                                            {channelMembers.map((member) => (
                                              <option key={member.user_id} value={member.user_id}>
                                                {member.name} ({member.email_id})
                                              </option>
                                            ))}
                                          </>
                                        )}
                                      </select>
                                    </Box>
                                 ) : (
                                   <TextField
                                     multiline
                                     rows={2}
                                     placeholder="Add a comment"
                                     value={rowComments[approval.id] || ''}
                                     onChange={(e) => setRowComments(prev => ({ ...prev, [approval.id]: e.target.value }))}
                                     fullWidth
                                     variant="outlined"
                                     size="small"
                                     sx={{
                                       '& .MuiOutlinedInput-root': {
                                         borderRadius: '6px',
                                         backgroundColor: isDarkMode ? 'grey.900' : '#fff'
                                       }
                                     }}
                                   />
                                 )}
                               </Box>
                             
                                                           {/* Save/Cancel Buttons */}
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleCancel(approval.id)}
                                  sx={{
                                    borderColor: 'divider',
                                    color: 'text.secondary',
                                    fontSize: '0.75rem',
                                    px: 1.5,
                                    py: 0.5
                                  }}
                                >
                                  Cancel
                                </Button>
                                                                <Button
                                   size="small"
                                   variant="contained"
                                                                       onClick={() => {
                                      const selectedAction = selectedActions[approval.id];
                                      if (selectedAction === 'approved') {
                                        handleApprove(approval.id);
                                      } else if (selectedAction === 'rejected') {
                                        handleReject(approval.id);
                                      } else if (selectedAction === 'reassign') {
                                        handleReassignSubmit(approval.id);
                                      }
                                    }}
                                    disabled={!selectedActions[approval.id] || 
                                      (selectedActions[approval.id] !== 'reassign' && !rowComments[approval.id]?.trim()) ||
                                      (selectedActions[approval.id] === 'reassign' && !rowComments[approval.id])}
                                   sx={{
                                     backgroundColor: '#1976d2',
                                     fontSize: '0.75rem',
                                     px: 1.5,
                                     py: 0.5,
                                     '&:hover': { backgroundColor: '#1565c0' }
                                   }}
                                 >
                                                                       {selectedActions[approval.id] ? 
                                      (selectedActions[approval.id] === 'approved' ? 'Approve' : 
                                       selectedActions[approval.id] === 'rejected' ? 'Reject' : 
                                       selectedActions[approval.id] === 'reassign' ? 'Reassign' : 'Save') : 
                                      'Save'
                                    }
                                 </Button>
                              </Box>
                           </Box>
                         </TableCell>
                       </TableRow>
                     )}
                 </React.Fragment>
               ))
             )}
            </TableBody>
          </Table>
                          </TableContainer>
                          
                          {/* Pagination */}
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            p: 2, 
                            borderTop: 1,
                            borderColor: 'divider',
                            backgroundColor: isDarkMode ? 'grey.800' : '#f8f9fa'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              {loading && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CircularProgress size={16} />
                                  <Typography variant="caption" color="text.secondary">
                                    Loading...
                                  </Typography>
                                </Box>
                              )}
                              <Typography variant="body2" color="text.secondary">
                                Showing {((pagination.page - 1) * pagination.size) + 1} to {Math.min(pagination.page * pagination.size, filteredApprovals.length)} of {filteredApprovals.length} results
                              </Typography>
                            </Box>
                            <TablePagination
                              component="div"
                              count={filteredApprovals.length}
                              page={pagination.page - 1} // MUI uses 0-based indexing
                              rowsPerPage={pagination.size}
                              onPageChange={handlePageChange}
                              onRowsPerPageChange={handlePageSizeChange}
                              rowsPerPageOptions={[10, 20, 50, 100]}
                              labelRowsPerPage="Rows per page:"
                              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                            />
                          </Box>
                        </Paper>

       {/* Time Filter Menu */}
       {/* Created By Filter Menu */}
<Menu
  anchorEl={createdByFilterAnchor}
  open={Boolean(createdByFilterAnchor)}
  onClose={() => setCreatedByFilterAnchor(null)}
  anchorOrigin={{
    vertical: 'bottom',
    horizontal: 'left',
  }}
  transformOrigin={{
    vertical: 'top',
    horizontal: 'left',
  }}
  PaperProps={{
    sx: {
      minWidth: '200px',
      maxHeight: '300px',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    }
  }}
>
  <MenuItem 
          onClick={() => {
        handleFilterChange('created_by', '');
        setCreatedByFilterAnchor(null);
      }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 16,
          height: 16,
          border: '1px solid #ccc',
          borderRadius: '2px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: !filters.created_by ? '#1976d2' : 'transparent'
        }}
      >
        {!filters.created_by && (
          <Box
            sx={{
              width: 10,
              height: 6,
              borderLeft: 2,
              borderBottom: 2,
              borderColor: 'background.paper',
              transform: 'rotate(-45deg)',
              marginTop: '-1px'
            }}
          />
        )}
      </Box>
      All
    </Box>
  </MenuItem>
  {getUniqueCreators().map((creator) => (
    <MenuItem 
      key={creator.id}
      onClick={() => {
        handleFilterChange('created_by', filters.created_by === creator.id ? '' : creator.id);
        setCreatedByFilterAnchor(null);
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 16,
            height: 16,
            border: '1px solid #ccc',
            borderRadius: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: filters.created_by === creator.id ? '#1976d2' : 'transparent'
          }}
        >
          {filters.created_by === creator.id && (
            <Box
              sx={{
                width: 10,
                height: 6,
                borderLeft: 2,
                borderBottom: 2,
                borderColor: 'background.paper',
                transform: 'rotate(-45deg)',
                marginTop: '-1px'
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar 
            sx={{ width: 24, height: 24, bgcolor: getAvatarColor(creator.name), fontSize: '12px' }}
          >
            {creator.name.charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="body2">{creator.name}</Typography>
        </Box>
      </Box>
    </MenuItem>
  ))}
</Menu>

       <Menu
  anchorEl={spaceFilterAnchor}
  open={Boolean(spaceFilterAnchor)}
  onClose={() => setSpaceFilterAnchor(null)}
  anchorOrigin={{
    vertical: 'bottom',
    horizontal: 'left',
  }}
  transformOrigin={{
    vertical: 'top',
    horizontal: 'left',
  }}
  PaperProps={{
    sx: {
      minWidth: '200px',
      maxHeight: '300px',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    }
  }}
>
  <MenuItem 
    onClick={() => {
      handleFilterChange('channel_id', '');
      setSpaceFilterAnchor(null);
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 16,
          height: 16,
          border: '1px solid #ccc',
          borderRadius: '2px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: !filters.channel_id ? '#1976d2' : 'transparent'
        }}
      >
        {!filters.channel_id && (
          <Box
            sx={{
              width: 10,
              height: 6,
              borderLeft: 2,
              borderBottom: 2,
              borderColor: 'background.paper',
              transform: 'rotate(-45deg)',
              marginTop: '-1px'
            }}
          />
        )}
      </Box>
      All
    </Box>
  </MenuItem>
  {getUniqueSpaces().map((space) => (
    <MenuItem 
      key={space.id}
      onClick={() => {
        handleFilterChange('channel_id', filters.channel_id === space.id ? '' : space.id);
        setSpaceFilterAnchor(null);
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 16,
            height: 16,
            border: '1px solid #ccc',
            borderRadius: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: filters.channel_id === space.id ? '#1976d2' : 'transparent'
          }}
        >
          {filters.channel_id === space.id && (
            <Box
              sx={{
                width: 10,
                height: 6,
                borderLeft: 2,
                borderBottom: 2,
                borderColor: 'background.paper',
                transform: 'rotate(-45deg)',
                marginTop: '-1px'
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon sx={{ width: 20, height: 20, color: 'primary.main' }} />
          <Typography variant="body2">{space.name}</Typography>
        </Box>
      </Box>
    </MenuItem>
  ))}
</Menu>

       <Menu
         anchorEl={timeFilterMenuAnchor}
         open={Boolean(timeFilterMenuAnchor)}
         onClose={() => {
           setTimeFilterMenuAnchor(null);
           setShowCustomRange(false);
           setCustomStartDate('');
           setCustomEndDate('');
         }}
         PaperProps={{
           sx: {
             minWidth: '200px',
             p: 1
           }
         }}
       >
         <MenuItem onClick={() => {
           handleFilterChange('timeFilter', 'Today');
           setTimeFilterMenuAnchor(null);
         }}>
           Today
         </MenuItem>
         <MenuItem onClick={() => {
           handleFilterChange('timeFilter', 'Yesterday');
           setTimeFilterMenuAnchor(null);
         }}>
           Yesterday
         </MenuItem>
         <MenuItem onClick={() => {
           handleFilterChange('timeFilter', 'Last 24 hrs');
           setTimeFilterMenuAnchor(null);
         }}>
           Last 24 hrs
         </MenuItem>
         <MenuItem onClick={() => {
           handleFilterChange('timeFilter', 'Last 48 hrs');
           setTimeFilterMenuAnchor(null);
         }}>
           Last 48 hrs
         </MenuItem>
         <MenuItem onClick={() => {
           handleFilterChange('timeFilter', 'Last 7 Days');
           setTimeFilterMenuAnchor(null);
         }}>
           Last 7 Days
         </MenuItem>
         <MenuItem onClick={() => {
           handleFilterChange('timeFilter', 'Last 30 Days');
           setTimeFilterMenuAnchor(null);
         }}>
           Last 30 Days
         </MenuItem>
         <MenuItem onClick={() => {
           handleFilterChange('timeFilter', 'This Month');
           setTimeFilterMenuAnchor(null);
         }}>
           This Month
         </MenuItem>
         <MenuItem onClick={() => {
           handleFilterChange('timeFilter', 'Last Month');
           setTimeFilterMenuAnchor(null);
         }}>
           Last Month
         </MenuItem>
         <MenuItem onClick={() => {
           handleFilterChange('timeFilter', 'All');
           setTimeFilterMenuAnchor(null);
         }}>
           All
         </MenuItem>
         <MenuItem 
           onClick={() => {
             setShowCustomRange(!showCustomRange);
           }}
           sx={{ 
             display: 'flex', 
             justifyContent: 'space-between', 
             alignItems: 'center' 
           }}
         >
           Custom Range
           <ArrowDownIcon fontSize="small" />
         </MenuItem>
         {showCustomRange && (
           <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
             <Box sx={{ mb: 2 }}>
               <TextField
                 type="date"
                 label="Start Date"
                 value={customStartDate}
                 onChange={(e) => setCustomStartDate(e.target.value)}
                 fullWidth
                 size="small"
                 InputProps={{
                   startAdornment: (
                     <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                   ),
                 }}
                 sx={{
                   '& .MuiInputBase-root': {
                     borderRadius: '8px',
                   }
                 }}
               />
             </Box>
             <Box sx={{ mb: 2 }}>
               <TextField
                 type="date"
                 label="End Date"
                 value={customEndDate}
                 onChange={(e) => setCustomEndDate(e.target.value)}
                 fullWidth
                 size="small"
                 InputProps={{
                   startAdornment: (
                     <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                   ),
                 }}
                 sx={{
                   '& .MuiInputBase-root': {
                     borderRadius: '8px',
                   }
                 }}
               />
             </Box>
             <Box sx={{ display: 'flex', gap: 1 }}>
               <Button
                 variant="contained"
                 size="small"
                 onClick={() => {
                   if (customStartDate && customEndDate) {
                     handleFilterChange('timeFilter', `Custom: ${customStartDate} to ${customEndDate}`);
                     setTimeFilterMenuAnchor(null);
                     setShowCustomRange(false);
                     setCustomStartDate('');
                     setCustomEndDate('');
                   }
                 }}
                 disabled={!customStartDate || !customEndDate}
                 sx={{ flex: 1 }}
               >
                 Apply
               </Button>
               <Button
                 variant="outlined"
                 size="small"
                 onClick={() => {
                   setShowCustomRange(false);
                   setCustomStartDate('');
                   setCustomEndDate('');
                 }}
                 sx={{ flex: 1 }}
               >
                 Cancel
               </Button>
             </Box>
           </Box>
         )}
       </Menu>

       {/* Status Filter Menu */}
      <Menu
        anchorEl={statusFilterAnchor}
        open={Boolean(statusFilterAnchor)}
        onClose={() => setStatusFilterAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleSelectAllStatuses}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                border: '1px solid #ccc',
                borderRadius: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: filters.status.length === 3 ? '#1976d2' : 'transparent'
              }}
            >
              {filters.status.length === 3 && (
                <Box
                  sx={{
                    width: 10,
                    height: 6,
                    borderLeft: '2px solid white',
                    borderBottom: '2px solid white',
                    transform: 'rotate(-45deg)',
                    marginTop: '-1px'
                  }}
                />
              )}
            </Box>
            All
          </Box>
        </MenuItem>
                 <MenuItem onClick={() => handleStatusToggle('pending')}>
           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
             <Box
               sx={{
                 width: 16,
                 height: 16,
                 border: '1px solid #ccc',
                 borderRadius: '2px',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 backgroundColor: filters.status.includes('pending') ? '#1976d2' : 'transparent'
               }}
             >
               {filters.status.includes('pending') && (
                 <Box
                   sx={{
                     width: 10,
                     height: 6,
                     borderLeft: 2,
                     borderBottom: 2,
                     borderColor: 'background.paper',
                     transform: 'rotate(-45deg)',
                     marginTop: '-1px'
                   }}
                 />
               )}
             </Box>
             Pending
           </Box>
         </MenuItem>
         <MenuItem onClick={() => handleStatusToggle('approved')}>
           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
             <Box
               sx={{
                 width: 16,
                 height: 16,
                 border: '1px solid #ccc',
                 borderRadius: '2px',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 backgroundColor: filters.status.includes('approved') ? '#1976d2' : 'transparent'
               }}
             >
               {filters.status.includes('approved') && (
                 <Box
                   sx={{
                     width: 10,
                     height: 6,
                     borderLeft: 2,
                     borderBottom: 2,
                     borderColor: 'background.paper',
                     transform: 'rotate(-45deg)',
                     marginTop: '-1px'
                   }}
                 />
               )}
             </Box>
             Approved
           </Box>
         </MenuItem>
         <MenuItem onClick={() => handleStatusToggle('rejected')}>
           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
             <Box
               sx={{
                 width: 16,
                 height: 16,
                 border: '1px solid #ccc',
                 borderRadius: '2px',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 backgroundColor: filters.status.includes('rejected') ? '#1976d2' : 'transparent'
               }}
             >
               {filters.status.includes('rejected') && (
                 <Box
                   sx={{
                     width: 10,
                     height: 6,
                     borderLeft: 2,
                     borderBottom: 2,
                     borderColor: 'background.paper',
                     transform: 'rotate(-45deg)',
                     marginTop: '-1px'
                   }}
                 />
               )}
             </Box>
             Rejected
           </Box>
         </MenuItem>
        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1, mt: 1 }}>
          <MenuItem onClick={() => setStatusFilterAnchor(null)}>
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
              Close
            </Typography>
          </MenuItem>
        </Box>
      </Menu>

      {/* Assignee Filter Menu */}
      <Menu
        anchorEl={assigneeFilterAnchor}
        open={Boolean(assigneeFilterAnchor)}
        onClose={() => setAssigneeFilterAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            minWidth: '200px',
            maxHeight: '300px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }
        }}
      >
        <MenuItem 
    onClick={() => {
      handleFilterChange('requested_by', '');
      setAssigneeFilterAnchor(null);
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 16,
          height: 16,
          border: '1px solid #ccc',
          borderRadius: '2px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: !filters.requested_by ? '#1976d2' : 'transparent'
        }}
      >
        {!filters.requested_by && (
          <Box
            sx={{
              width: 10,
              height: 6,
              borderLeft: 2,
              borderBottom: 2,
              borderColor: 'background.paper',
              transform: 'rotate(-45deg)',
              marginTop: '-1px'
            }}
          />
        )}
      </Box>
      All
    </Box>
  </MenuItem>
  {getUniqueRequesters().map((requester) => (
    <MenuItem 
      key={requester.id}
              onClick={() => {
          handleFilterChange('requested_by', filters.requested_by === requester.id ? '' : requester.id);
          setAssigneeFilterAnchor(null);
        }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 16,
            height: 16,
            border: '1px solid #ccc',
            borderRadius: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: filters.requested_by === requester.id ? '#1976d2' : 'transparent'
          }}
        >
          {filters.requested_by === requester.id && (
            <Box
              sx={{
                width: 10,
                height: 6,
                borderLeft: 2,
                borderBottom: 2,
                borderColor: 'background.paper',
                transform: 'rotate(-45deg)',
                marginTop: '-1px'
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar 
            sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: '12px' }}
          >
            {/* {approver.charAt(0).toUpperCase()} */}
          </Avatar>
          <Typography variant="body2">{requester.name}</Typography>
        </Box>
      </Box>
    </MenuItem>
  ))}
</Menu>

             {/* Status Dropdown Menu for Table Rows */}
       <Menu
         anchorEl={statusDropdownAnchor}
         open={Boolean(statusDropdownAnchor)}
         onClose={() => {
           setStatusDropdownAnchor(null);
           setSelectedApprovalId(null);
         }}
         anchorOrigin={{
           vertical: 'bottom',
           horizontal: 'left',
         }}
         transformOrigin={{
           vertical: 'top',
           horizontal: 'left',
         }}
         PaperProps={{
           sx: {
             minWidth: '150px',
             borderRadius: '8px',
             boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
           }
         }}
       >
         {(() => {
           const currentApproval = allApprovals.find(approval => approval.id === selectedApprovalId);
           const currentStatus = currentApproval?.approval_details?.status || 'pending';
           
           // Don't show dropdown for "approved" or "rejected" status
           if (currentStatus === 'approved' || currentStatus === 'rejected') {
             return null;
           }
           
           // Show different options based on current status
           if (currentStatus === 'pending') {
             return (
               <>
                                   <MenuItem 
                    onClick={() => {
                      if (selectedApprovalId) {
                        // Only set the action, don't call API yet
                        setSelectedActions(prev => ({ ...prev, [selectedApprovalId]: 'approved' }));
                        // Clear any previous reassign data only if it's not already reassign
                        setRowComments(prev => ({ ...prev, [selectedApprovalId]: '' }));
                        // Expand the row to show action buttons
                        setSelectedStatuses(prev => ({ ...prev, [selectedApprovalId]: 'approved' }));
                      }
                      setStatusDropdownAnchor(null);
                      setSelectedApprovalId(null);
                    }}
                    sx={{
                      backgroundColor: isDarkMode ? 'grey.700' : '#e8f5e8',
                      color: isDarkMode ? '#81c784' : '#2e7d32',
                      fontWeight: 'medium',
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'grey.600' : '#c8e6c9'
                      }
                    }}
                  >
                    Approve
                  </MenuItem>
                  <MenuItem 
                    onClick={() => {
                      if (selectedApprovalId) {
                        // Only set the action, don't call API yet
                        setSelectedActions(prev => ({ ...prev, [selectedApprovalId]: 'rejected' }));
                        // Clear any previous reassign data
                        setRowComments(prev => ({ ...prev, [selectedApprovalId]: '' }));
                        // Expand the row to show action buttons
                        setSelectedStatuses(prev => ({ ...prev, [selectedApprovalId]: 'rejected' }));
                      }
                      setStatusDropdownAnchor(null);
                      setSelectedApprovalId(null);
                    }}
                    sx={{
                      backgroundColor: isDarkMode ? 'grey.700' : '#ffebee',
                      color: '#ef5350',
                      fontWeight: 'medium',
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'grey.600' : '#ffcdd2'
                      }
                    }}
                  >
                    Reject
                  </MenuItem>
               </>
             );
           }
           
                       return null;
          })()}
        </Menu>

        {/* Channel Members Menu for Reassign */}
        <Menu
          anchorEl={membersMenuAnchor}
          open={Boolean(membersMenuAnchor)}
          onClose={() => {
            setMembersMenuAnchor(null);
            setSelectedApprovalForReassign(null);
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              minWidth: '250px',
              maxHeight: '300px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }
          }}
        >
          <Box sx={{ p: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'medium', mb: 1, px: 1 }}>
              Select Member to Reassign
            </Typography>
            {channelMembers.length === 0 ? (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  No members found
                </Typography>
              </MenuItem>
            ) : (
                             channelMembers.map((member) => (
                 <MenuItem
                   key={member.user_id}
                   onClick={() => handleMemberSelect(member.user_id, member.name)}
                   sx={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: 1,
                     py: 1,
                     '&:hover': {
                       backgroundColor: isDarkMode ? 'grey.700' : '#f5f5f5'
                     }
                   }}
                 >
                   <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: '10px' }}>
                     {member.name.charAt(0).toUpperCase()}
                   </Avatar>
                   <Box>
                     <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                       {member.name}
                     </Typography>
                     <Typography variant="caption" color="text.secondary">
                       {member.email_id}
                     </Typography>
                   </Box>
                 </MenuItem>
               ))
            )}
          </Box>
        </Menu>
     </Box>
   );
 };

export default ApprovalsPage;
